/**
 * commitSharePlan — turns a ShareSheet `SharePlan` into the real API
 * calls, with NO backend changes (all endpoints already exist).
 *
 * Two entry points:
 *   - createPendingGroups()        : create each "share with someone new"
 *                                    group + invite the email (lazy — only
 *                                    runs at commit time per Q1). Returns the
 *                                    created groups with their real ids so
 *                                    the caller can bind items.
 *   - commitSharePlanToServer()    : scene / ItemDetail path. Creates new
 *                                    groups, then diffs each existing group's
 *                                    selection against what was already
 *                                    shared (POST adds, DELETE removes).
 *
 * The scan-save path does NOT use commitSharePlanToServer — items aren't
 * persisted yet, so SegmentScreen calls createPendingGroups() to resolve
 * ids and then threads per-item `group_ids` into confirm/batch `extras`.
 */

import type { AppDispatch } from '../../store/store';
import {
  createGroup,
  inviteGroupMember,
  addItemsToGroup,
  removeItemFromGroup,
} from './groupsSlice';
import type { SharePlan, PendingNewGroup } from './components/ShareSheet';

export interface CreatedGroup extends PendingNewGroup {
  groupId: number;
}

export interface ShareCommitResult {
  added: number; // item-bindings created
  removed: number; // item-bindings deleted
  groupsCreated: number;
  failures: string[]; // human-readable, surfaced via toast
}

const errText = (e: any): string =>
  typeof e === 'string' ? e : e?.detail || e?.message || 'please try again';

/**
 * Create each staged new-person group + invite the email. Best-effort:
 * a failed invite does NOT discard the created group (the owner can
 * resend), and a failed create just drops that one group with a logged
 * failure — the rest still proceed.
 */
export async function createPendingGroups(
  dispatch: AppDispatch,
  pendingNew: PendingNewGroup[],
): Promise<{ created: CreatedGroup[]; failures: string[] }> {
  const created: CreatedGroup[] = [];
  const failures: string[] = [];

  for (const p of pendingNew) {
    try {
      const group = await dispatch(
        createGroup({ name: p.name, kind: p.kind }),
      ).unwrap();
      try {
        await dispatch(
          inviteGroupMember({
            groupId: group.id,
            request: { invitee_email: p.email },
          }),
        ).unwrap();
      } catch (e) {
        failures.push(`Couldn't invite ${p.email} (${errText(e)})`);
      }
      created.push({ ...p, groupId: group.id });
    } catch (e) {
      failures.push(`Couldn't create "${p.name}" (${errText(e)})`);
    }
  }

  return { created, failures };
}

/**
 * Scene / ItemDetail commit. Creates new-person groups, then for each
 * existing group POSTs the newly-added items and DELETEs the
 * newly-removed ones (diffing against `initialShares`). Created groups
 * get all their picked items POSTed.
 */
export async function commitSharePlanToServer(
  dispatch: AppDispatch,
  plan: SharePlan,
  initialShares: Record<number, number[]>,
): Promise<ShareCommitResult> {
  const res: ShareCommitResult = {
    added: 0,
    removed: 0,
    groupsCreated: 0,
    failures: [],
  };

  const { created, failures } = await createPendingGroups(
    dispatch,
    plan.pendingNew,
  );
  res.failures.push(...failures);
  res.groupsCreated = created.length;

  // Existing groups → diff selection vs already-shared.
  for (const [gidStr, selectedIds] of Object.entries(plan.selections)) {
    const gid = Number(gidStr);
    const initial = new Set(initialShares[gid] ?? []);
    const sel = new Set(selectedIds);
    const toAdd = selectedIds.filter((id) => !initial.has(id));
    const toRemove = [...initial].filter((id) => !sel.has(id));

    if (toAdd.length > 0) {
      try {
        await dispatch(addItemsToGroup({ groupId: gid, itemIds: toAdd })).unwrap();
        res.added += toAdd.length;
      } catch (e) {
        res.failures.push(`Couldn't share ${toAdd.length} item(s) (${errText(e)})`);
      }
    }
    for (const itemId of toRemove) {
      try {
        await dispatch(removeItemFromGroup({ groupId: gid, itemId })).unwrap();
        res.removed += 1;
      } catch (e) {
        res.failures.push(`Couldn't unshare an item (${errText(e)})`);
      }
    }
  }

  // Created groups → bind their picked items.
  for (const c of created) {
    if (c.itemIds.length > 0) {
      try {
        await dispatch(
          addItemsToGroup({ groupId: c.groupId, itemIds: c.itemIds }),
        ).unwrap();
        res.added += c.itemIds.length;
      } catch (e) {
        res.failures.push(`Couldn't add items to "${c.name}" (${errText(e)})`);
      }
    }
  }

  return res;
}
